import type { HeatDemandData } from '../types';

const FORM_LABELS: Record<string, string> = {
  'detached': 'Detached',
  'semi-detached': 'Semi-detached',
  'mid-terrace': 'Mid-terrace',
  'end-terrace': 'End-terrace',
  'flat-top': 'Flat (top)',
  'flat-mid': 'Flat (mid)',
  'bungalow': 'Bungalow',
  'unknown': 'Unknown',
};

const ERA_LABELS: Record<string, string> = {
  'pre-1919': 'Pre-1919',
  '1919-1944': '1919–1944',
  '1945-1964': '1945–1964',
  '1965-1980': '1965–1980',
  '1981-1995': '1981–1995',
  '1996-2006': '1996–2006',
  '2007-2013': '2007–2013',
  '2013-present': '2013+',
  'unknown': 'Unknown',
};

const RETROFIT_LABELS: Record<string, string> = {
  none: 'None',
  partial: 'Partial',
  full: 'Full',
  unknown: 'Unknown',
};

const ARCHETYPE_LABELS: Record<string, string> = {
  standard: 'Standard',
  'timber-frame': 'Timber frame',
  'concrete-panel': 'Concrete panel',
  prefab: 'Post-war prefab',
  'park-home': 'Park home',
  'solid-wall': 'Solid wall',
  unknown: 'Unknown',
};

const BAND_LABELS: Record<string, string> = {
  'very-low': 'Very low',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  'very-high': 'Very high',
  unknown: '—',
};

interface Props {
  data: HeatDemandData;
  onClick: () => void;
}

export function HeatDemandTile({ data, onClick }: Props) {
  const v = (val: string, map: Record<string, string>) =>
    map[val] ?? val ?? '—';
  const isUnknown = (val: string) => !val || val === 'unknown';

  return (
    <div className="hub-tile" onClick={onClick} role="button" tabIndex={0}>
      <div className="hub-tile__header">
        <h3 className="hub-tile__title">Heat Demand</h3>
        <span className={`hub-tile__badge hub-tile__badge--${data.confidence.level}`}>
          {data.confidence.level}
        </span>
      </div>
      <div className="hub-tile__body">
        <div className="hub-tile__row">
          <span className="hub-tile__label">Dwelling</span>
          <span className={`hub-tile__value${isUnknown(data.dwellingForm) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.dwellingForm, FORM_LABELS)}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Bedrooms</span>
          <span className={`hub-tile__value${!data.bedrooms ? ' hub-tile__value--unknown' : ''}`}>
            {data.bedrooms || '—'}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Fabric era</span>
          <span className={`hub-tile__value${isUnknown(data.fabricEra) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.fabricEra, ERA_LABELS)}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Retrofit</span>
          <span className={`hub-tile__value${isUnknown(data.retrofitLevel) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.retrofitLevel, RETROFIT_LABELS)}
          </span>
        </div>
        {data.constructionArchetype && data.constructionArchetype !== 'standard' && data.constructionArchetype !== 'unknown' && (
          <div className="hub-tile__row">
            <span className="hub-tile__label">Construction</span>
            <span className="hub-tile__value">{v(data.constructionArchetype, ARCHETYPE_LABELS)}</span>
          </div>
        )}
        <div className="hub-tile__row">
          <span className="hub-tile__label">Heat loss band</span>
          <span className={`hub-tile__value${isUnknown(data.heatLossBand) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.heatLossBand, BAND_LABELS)}
          </span>
        </div>
        {data.knownHeatLossKw !== undefined && (
          <div className="hub-tile__row">
            <span className="hub-tile__label">Known heat loss</span>
            <span className="hub-tile__value">{data.knownHeatLossKw} kW</span>
          </div>
        )}
      </div>

      {data.constructionArchetype && data.constructionArchetype !== 'standard' && data.constructionArchetype !== 'unknown' && (
        <div className="hub-tile__warnings">
          <span className="hub-tile__warning">Special construction: {v(data.constructionArchetype, ARCHETYPE_LABELS)}</span>
        </div>
      )}

      <span className="hub-tile__tap-hint">tap to edit</span>
    </div>
  );
}
